/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {stringify} from '../util';
import {resolveForwardRef} from './forward_ref';
/**
 * A unique object used for retrieving items from the {\@link ReflectiveInjector}.
 * 
 * Keys have:
 * - a system-wide unique `id`.
 * - a `token`.
 * 
 * `Key` is used internally by {\@link ReflectiveInjector} because its system-wide unique `id` allows
 * the
 * injector to store created objects in a more efficient way.
 * 
 * `Key` should not be created directly. {\@link ReflectiveInjector} creates keys automatically when
 * resolving
 * providers.
 * \@experimental
 */
export class ReflectiveKey {
/**
 * Private
 * @param {?} token
 * @param {?} id
 */
constructor(public token: Object,
public id: number) {
    if (!token) {
      throw new Error('Token must be defined!');
    }
  }
/**
 * Returns a stringified token.
 * @return {?}
 */
get displayName(): string { return stringify(this.token); }
/**
 * Retrieves a `Key` for a token.
 * @param {?} token
 * @return {?}
 */
static get(token: Object): ReflectiveKey {
    return _globalKeyRegistry.get(resolveForwardRef(token));
  }
/**
 * @return {?} the number of keys registered in the system.
 */
static get numberOfKeys(): number { return _globalKeyRegistry.numberOfKeys; }
}

function ReflectiveKey_tsickle_Closure_declarations() {
/** @type {?} */
ReflectiveKey.prototype.token;
/** @type {?} */
ReflectiveKey.prototype.id;
}

/**
 * \@internal
 */
export class KeyRegistry {
private _allKeys = new Map<Object, ReflectiveKey>();
/**
 * @param {?} token
 * @return {?}
 */
get(token: Object): ReflectiveKey {
    if (token instanceof ReflectiveKey) return token;

    if (this._allKeys.has(token)) {
      return /** @type {?} */(( this._allKeys.get(token)));
    }

    const /** @type {?} */ newKey = new ReflectiveKey(token, ReflectiveKey.numberOfKeys);
    this._allKeys.set(token, newKey);
    return newKey;
  }
/**
 * @return {?}
 */
get numberOfKeys(): number { return this._allKeys.size; }
}

function KeyRegistry_tsickle_Closure_declarations() {
/** @type {?} */
KeyRegistry.prototype._allKeys;
}


const /** @type {?} */ _globalKeyRegistry = new KeyRegistry();



// WEBPACK FOOTER //
// ../../../../packages/core/src/di/reflective_key.ts